import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type ShellNavContextValue = {
  isSidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
};

const ShellNavContext = createContext<ShellNavContextValue | null>(null);

export const ShellNavProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const openSidebar = useCallback(() => setIsSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);
  const toggleSidebar = useCallback(() => setIsSidebarOpen((v) => !v), []);

  const value = useMemo(
    () => ({ isSidebarOpen, openSidebar, closeSidebar, toggleSidebar }),
    [isSidebarOpen, openSidebar, closeSidebar, toggleSidebar],
  );

  return <ShellNavContext.Provider value={value}>{children}</ShellNavContext.Provider>;
};

export function useShellNav(): ShellNavContextValue {
  const ctx = useContext(ShellNavContext);
  if (!ctx) {
    throw new Error('useShellNav must be used within ShellNavProvider');
  }
  return ctx;
}

/** Optional — returns null outside provider (e.g. portal pages). */
export function useShellNavOptional(): ShellNavContextValue | null {
  return useContext(ShellNavContext);
}
